import { Title, Group, Button, Switch, Stack, ActionIcon } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { rulesApi } from "../api/rules.js";
import { notifications } from "@mantine/notifications";
import type { Rule } from "@cas/shared";

function summarize(r: Rule): string {
  const parts: string[] = [];
  if (r.senderMatch && r.senderValue)         parts.push(`sender ${r.senderMatch} "${r.senderValue}"`);
  if (r.subjectMatch && r.subjectValues?.length) parts.push(`subject ${r.subjectMatch} ${JSON.stringify(r.subjectValues)}`);
  if (r.bodyMatch && r.bodyValues?.length)    parts.push(`body ${r.bodyMatch} ${JSON.stringify(r.bodyValues)}`);
  return parts.join(` ${r.combineWith} `);
}

function actionSummary(r: Rule): string {
  const a: string[] = [];
  if (!r.forwardToSupport) a.push("don't forward");
  if (r.markAsRead) a.push("mark read");
  if (r.overrideFolderId) a.push(`folder → ${r.overrideFolderId.slice(0, 8)}…`);
  return a.length ? a.join(", ") : "(no overrides — default forward to support)";
}

export function RulesList() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: rules, isLoading } = useQuery({ queryKey: ["rules"], queryFn: rulesApi.list });

  const toggle = useMutation({
    mutationFn: (r: Rule) => rulesApi.update(r.id, { ...r, enabled: !r.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => rulesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      notifications.show({ title: "Deleted", message: "Rule removed", color: "yellow" });
    },
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Rules ({rules?.length ?? 0})</Title>
        <Button component={Link} to="/rules/new" leftSection={<IconPlus size={16} />}>Add rule</Button>
      </Group>
      <DataTable
        striped withTableBorder highlightOnHover
        records={rules ?? []}
        fetching={isLoading}
        idAccessor="id"
        columns={[
          {
            accessor: "enabled",
            title: "On",
            width: 60,
            render: (r) => (
              <Switch checked={r.enabled} onChange={() => toggle.mutate(r)} />
            ),
          },
          { accessor: "name", title: "Name", width: 200 },
          { accessor: "matches", title: "Match", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{summarize(r)}</span> },
          { accessor: "action", title: "Action", render: (r) => actionSummary(r) },
          {
            accessor: "actions", title: "", width: 120, textAlign: "right",
            render: (r) => (
              <Group gap="xs" justify="flex-end">
                <ActionIcon variant="subtle" onClick={() => nav(`/rules/${r.id}`)} title="Edit"><IconPencil size={16} /></ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => {
                  if (confirm(`Delete rule "${r.name}"?`)) remove.mutate(r.id);
                }} title="Delete"><IconTrash size={16} /></ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
}
